import { yupResolver } from '@hookform/resolvers/yup'
import { AxiosError } from 'axios'
import {
  Button,
  FormLayout,
  MultiSelectDropdown,
  Spinner,
} from 'components/common'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import {
  createFtpConfigurations,
  fetchStoresNotInFtp,
} from 'utils/api/ftpConfigurationApi'
import { ERROR_CODES } from 'utils/constants'
import { GenerateNewFTPSchema } from 'utils/schema/ftpConfigurationSchema'

export type CreateNewFtpData = {
  stores: (string | undefined)[]
  aggregators: (string | undefined)[]
}
type Props = {
  aggregators: any[]
  setRefetchConfig: React.Dispatch<React.SetStateAction<boolean>>
}

const NewFtpGenerationForm = ({ aggregators, setRefetchConfig }: Props) => {
  const {
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CreateNewFtpData>({ resolver: yupResolver(GenerateNewFTPSchema) })

  const [loading, setLoading] = useState<boolean>(true)
  const [actionLoading, setActionLoading] = useState<boolean>(false)
  const [resetForm, setResetForm] = useState<boolean>(false)

  // Store dropdown options
  const [storeOptions, setStoreOptions] = useState<any[]>([])

  // Aggregator dropdown options
  const aggregatorOptions = useMemo(() => {
    return aggregators?.map((aggregator: any) => ({
      label: aggregator.aggregatorName,
      value: aggregator._id,
      disabled: storeOptions?.length > 0 ? false : true,
    }))
  }, [aggregators, storeOptions?.length])

  // fetch store and aggregators to display in dropdown
  const getStores = async () => {
    try {
      const storeData = await fetchStoresNotInFtp()
      const storeOptions = storeData?.data?.map((store: any) => ({
        label: store.storeName,
        value: store._id,
        disabled: false,
      }))
      setStoreOptions(storeOptions)
    } catch (error) {
      setStoreOptions([
        { label: 'No Store Found', value: 'NO_STORE', disabled: true },
      ])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getStores()
  }, [resetForm])

  const handleCreateFtpConfiguration = async (data: CreateNewFtpData) => {
    const ftpToast = toast.loading('Creating Configuration...')
    setActionLoading(true)

    try {
      await createFtpConfigurations(data)
      setRefetchConfig((pre) => !pre)
      setResetForm((pre) => !pre)
      reset()
      toast.success('Configurations created successfully', { id: ftpToast })
    } catch (error) {
      if (error instanceof AxiosError) {
        const MESSAGE = error?.response?.data?.errors?.msg

        if (MESSAGE === ERROR_CODES.STORE_ALREADY_EXISTS) {
          return toast.error('Selected stores config already exists!', {
            id: ftpToast,
            duration: 3000,
          })
        }
      }
      toast.error('Failed to create Configuration', { id: ftpToast })
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <FormLayout>
      <form onSubmit={handleSubmit(handleCreateFtpConfiguration)}>
        <>
          <h4 className="text-xl font-semibold">New FTP Generations</h4>
          {loading ? (
            <div className="flex min-h-20 w-full flex-col items-center justify-center">
              <Spinner size="large" />
            </div>
          ) : (
            <div className="mt-3 flex max-w-3xl flex-wrap gap-x-16 gap-y-5">
              <div className="w-72">
                <MultiSelectDropdown
                  required
                  label="Stores"
                  setValue={setValue}
                  name="stores"
                  error={errors?.stores?.message}
                  includeSelectAll={storeOptions.length > 0}
                  options={
                    storeOptions.length > 0
                      ? storeOptions
                      : [
                          {
                            label: 'No Store Found',
                            value: 'NO_STORE',
                            disabled: true,
                          },
                        ]
                  }
                  reset={resetForm}
                />
              </div>
              <div className="w-72">
                <MultiSelectDropdown
                  required
                  label="Aggregators"
                  setValue={setValue}
                  name="aggregators"
                  error={errors?.aggregators?.message}
                  includeSelectAll={
                    aggregatorOptions.length > 0 && storeOptions?.length > 0
                  }
                  options={
                    aggregatorOptions?.length > 0
                      ? aggregatorOptions
                      : [
                          {
                            label: 'No Aggregators Found',
                            value: 'NO_AGGREGATORS',
                            disabled: true,
                          },
                        ]
                  }
                  reset={resetForm}
                />
              </div>
            </div>
          )}

          <div className="mt-8">
            <Button
              buttonText="Save"
              className="w-40 px-0"
              loading={actionLoading}
              disabled={loading || storeOptions?.length === 0}
            />
          </div>
        </>
      </form>
    </FormLayout>
  )
}

export default NewFtpGenerationForm
